;(() => {
    class CampaignDesignerService {

        constructor(APIService) {
            this.apiService = APIService;
            this.campaignTemplate = undefined;
        }

        /**
         * Gets the list of campaign templates by state
         * @param templateState
         * @param brandId
         * @returns {array}
         */
        getCampaignTemplatesByState(templateState, brandId) {
            return this.apiService.CampaignDesigner
                .getCampaignTemplatesByState(templateState,
                    brandId);
        }

        /**
         * Gets a campaign template using the guid provided.
         * @param {string} campaignTemplateGuid
         * @returns {Dashboard.Models.CampaignTemplate}
         */
        getCampaignTemplate(campaignTemplateGuid) {
            return this.apiService.CampaignDesigner.getCampaignTemplate(campaignTemplateGuid)
        };

        /**
         * Updates the campaign template store associations
         * @param campaignTemplateGuid
         * @param stores
         * @param brandId
         * @returns {*|promise}
         */
        updateCampaignTemplateStores(campaignTemplateGuid,
                                     stores,
                                     brandId) {
            return this.apiService.CampaignDesigner
                .updateCampaignTemplateStores(campaignTemplateGuid,
                    stores,
                    brandId);
        };

        /**
         * Updates the campaign template communication
         * @param campaignTemplateGuid
         * @param communication
         * @param limitToSingleCommunication
         */
        updateCampaignTemplateCommunication(campaignTemplateGuid,
                                            communication,
                                            limitToSingleCommunication) {
            return this.apiService.CampaignDesigner.updateCampaignTemplateCommunication(
                campaignTemplateGuid,
                communication,
                limitToSingleCommunication);
        };

        /**
         * Gets the inbox templates for the brand
         * @param brandId
         * @returns {array}
         */
        getInboxTemplatesForBrand(brandId) {
            return this.apiService.Templates.getInboxTemplatesForBrand(brandId);
        };

        /**
         * Saves the basic setup.
         * @param {CampaignTemplate} campaignTemplate
         */
        saveBasicSetup(campaignTemplate) {
            return this.apiService.CampaignDesigner.saveBasicSetup(campaignTemplate);
        }

        /**
         * Saves the given campaign template voucher to the campaign template
         * @param campaignTemplateGuid {string}
         * @param campaignTemplateVoucher {CampaignVoucherTemplate}
         */
        saveCampaignTemplateVoucher(campaignTemplateGuid,
                                    campaignTemplateVoucher) {
            return this.apiService.CampaignDesigner
                .saveCampaignTemplateVoucher(campaignTemplateGuid,
                    campaignTemplateVoucher);
        }

        /**
         * Removes the given campaign template voucher from the campaign template
         * @param campaignTemplateGuid {string}
         * @param campaignTemplateVoucherId {number}
         */
        removeCampaignTemplateVoucher(campaignTemplateGuid, campaignTemplateVoucherId) {
            return this.apiService.CampaignDesigner.removeCampaignTemplateVoucher(campaignTemplateGuid, campaignTemplateVoucherId);
        }

        /**
         * Saves the given campaign template menuscript to the campaign template
         * @param campaignTemplateGuid {string}
         * @param campaignTemplateMenuScript {CampaignMenuscriptTemplate}
         */
        saveCampaignTemplateMenuscript(campaignTemplateGuid, campaignTemplateMenuScript) {
            return this.apiService.CampaignDesigner.saveCampaignTemplateMenuscript(campaignTemplateGuid, campaignTemplateMenuScript);
        }

        /**
         * Removes the given campaign template menuscript from the campaign template
         * @param campaignTemplateGuid {string}
         * @param campaignTemplateMenuScriptId {number}
         */
        removeCampaignTemplateMenuscript(campaignTemplateGuid, campaignTemplateMenuScriptId) {
            return this.apiService.CampaignDesigner.removeCampaignTemplateMenuscript(campaignTemplateGuid, campaignTemplateMenuScriptId);
        }

        /**
         * Gets the customer workflow types
         * @returns {array}
         */
        getCustomerWorkflowTypes() {
            return this.apiService.CampaignDesigner.getCustomerWorkflowTypes();
        }

        /**
         * Gets the date rules for the campaign templates
         * @returns {array}
         */
        getCampaignTemplateDateRules() {
            return Dashboard.Models.Lookups.DateRules;
        };

        /**
         * Activates the campaign template.
         * @param {string} campaignTemplateGuid
         */
        publishCampaignTemplate(campaignTemplateGuid) {
            return this.apiService.CampaignDesigner.publishCampaignTemplate(campaignTemplateGuid);
        }

        /**
         * Gets the campaign objectives
         * @returns {array}
         */
        getCampaignObjectives() {
            return this.apiService.CampaignDesigner.getCampaignObjectives();
        }

        archiveCampaignTemplate(campaignTemplateGuid) {
            return this.apiService
                .CampaignDesigner
                .archiveCampaignTemplate(campaignTemplateGuid);
        }

        unArchiveCampaignTemplate(campaignTemplateGuid) {
            return this.apiService
                .CampaignDesigner
                .unArchiveCampaignTemplate(campaignTemplateGuid);
        }

        removeCampaignTemplateCommunication(campaignTemplateGuid, campaignTemplateCommunicationId) {
            return this.apiService.CampaignDesigner
                .removeCampaignTemplateCommunication(
                    campaignTemplateGuid,
                    campaignTemplateCommunicationId);
        }

        getTargetSourcesForFilter(take,
                                 skip,
                                 filter) {
            return this.apiService.CampaignDesigner.getTargetSourcesForFilter(
                take,
                skip,
                filter);
        }

        getTargetSourceById(targetSourceId) {
            return this.apiService.CampaignDesigner.getTargetSourceById(targetSourceId);
        }

        saveTargetSource(targetSource) {
            this.apiService.CampaignDesigner.saveTargetSource(targetSource);
        }

        async testTargetSource(
            source,
            brandId,
            testStoreId) {
            return await this.apiService.CampaignDesigner.testTargetSource(
                source,
                brandId,
                testStoreId);
        }

        uploadFeaturedImage(templateGuid, files) {
            if (!(files instanceof FileList)) {
                throw new Error("FileList expected")
            }
            return this.apiService.CampaignDesigner.uploadFeaturedImage(templateGuid, files)
        }

        clearFeaturedImage(templateGuid) {
            if (!templateGuid) {
                throw new Error("templateGuid is invalid")
            }
            return this.apiService.CampaignDesigner.clearFeaturedImage(templateGuid)
        }

        revertCampaignTemplateToDraft(id) {
            return this.apiService.Campaigns.revertCampaignTemplateToDraft(id);
        }
    }

    angular.module('dashboard')
        .service('CampaignDesignerService', ['APIService', CampaignDesignerService]);

})();
